import React, { createContext } from "react";
import type {
    AnnotationsForFile,
    AnnotationsForFileAndFrameId,
    PredictionsForFileAndFrameSegment,
    PredictionsForFile
} from "@/store";
import { PromptLabel, PromptLabels } from "@/store/slices/modelIntentSlice";

// === STATE / ACTIONS ===
export interface ModelState {
    isDownloading: boolean;
    downloadProgress: number;
    isTestingNewFiles: boolean;
    testingProgress: number;
    abortController: AbortController | null;
    annotationsForFileAndFrameId: AnnotationsForFileAndFrameId;
    predictionsForFileAndFrameSegment: PredictionsForFileAndFrameSegment;
    promptLabels: PromptLabels;
    // approved files tracking
    approvedFileIds: Set<string>;
    // computed for current file
    annotationsForFile: AnnotationsForFile;
    predictionsForFile: PredictionsForFile;
    // global state
    hasAnnotations: boolean;
    hasPredictions: boolean;

    textPromptStr: string;
    textPrompts: string[];
}

export interface ModelActions {
    setAnnotationsForFileAndFrameId: React.Dispatch<
        React.SetStateAction<AnnotationsForFileAndFrameId>
    >;
    setPredictionsForFileAndFrameSegment: React.Dispatch<
        React.SetStateAction<PredictionsForFileAndFrameSegment>
    >;
    setPromptLabels: (labels: PromptLabels) => void;
    updatePromptLabels: (labels: PromptLabel[], labelsToDelete?: string[]) => void;
    setPromptLabelsGlobalConfidenceThreshold: (confidenceThreshold: number) => void;
    // approved files management
    markFileAsApproved: (fileId: string) => void;
    isFileApproved: (fileId: string) => boolean;
    // helpers
    clearAnnotationsForFile: (fileId: string) => void;
    clearPredictionsForFile: (fileId: string) => void;
    clearPredictionsForFileAndFrameSegment: (fileId: string, frameSegment: number) => void;
    setTextPromptStr: React.Dispatch<React.SetStateAction<string>>;
    setIsDownloading: React.Dispatch<React.SetStateAction<boolean>>;
    setDownloadProgress: React.Dispatch<React.SetStateAction<number>>;
    setIsTestingNewFiles: React.Dispatch<React.SetStateAction<boolean>>;
    setTestingProgress: React.Dispatch<React.SetStateAction<number>>;
    setAbortController: React.Dispatch<React.SetStateAction<AbortController | null>>;
}

export interface ModelContextType extends ModelState, ModelActions {}

export const ModelContext = createContext<ModelContextType | undefined>(undefined);

// === HOOKS ===
export function useModel(): ModelContextType {
    const ctx = React.useContext(ModelContext);
    if (!ctx) throw new Error("useModel must be used within an ModelProvider");
    return ctx;
}
